import _ from 'lodash';
import BaseChartModel from './BaseChartModel';

const similarAreaLabel = 'Similar Area';
const defaultOptions = {
    metricColumn: 'metric',
    titleColumn: 'label',
    positiveDirection: 1,
    format: value => `${(value * 100).toFixed(0)}`,
    units: '%',
    domain: [0, 1],
    chartType: 'dotPlot',
    isPrimary: false,
    showZoneData: false,
    excludeNulls: false
};

/* eslint-disable no-underscore-dangle */
export default class MetricChartModel extends BaseChartModel {
    constructor(dataRecords, parent, options) {
        super(dataRecords, parent, { ...defaultOptions, ...options });
    }

    get allRecords() {
        return _(this.rawDataRecords)
            .filter(({ value }) => !(value === null && this.options.excludeNulls))
            .sortBy(({ value }) => {
                if (value === null) {
                    return Infinity;
                }
                return value * this.options.positiveDirection;
            })
            .map(({ geographyCode, ...rest }) => ({
                // eslint-disable-next-line no-nested-ternary
                type: geographyCode === this.valueGeographyCode ? 'value'
                    : geographyCode === this.parent.comparisonGeographyCode ? 'comparison'
                        : 'none',
                geographyCode,
                ...rest
            }))
            .value();
    }

    get localRecords() {
        return this.allRecords.filter(({ geographyCode }) => geographyCode === this.parent.comparisonGeographyCode || geographyCode.indexOf('.') !== -1);
    }

    get valueRecord() {
        return this.allRecords
            .find(({ geographyCode }) => this.valueGeographyCode === geographyCode);
    }

    get value() {
        return this.valueRecord && this.valueRecord.value;
    }

    get comparisonRecord() {
        return this.allRecords
            .find(({ geographyCode }) => this.comparisonGeographyCode === geographyCode);
    }

    get comparisonValue() {
        return this.comparisonRecord && this.comparisonRecord.value;
    }

    get target() {
        return this.options.target;
    }

    get zoneRecord() {
        return this.allRecords
            .find(({ geographyCode }) => this.parent.zoneGeographyCode === geographyCode);
    }

    get zoneValue() {
        return this.zoneRecord && this.zoneRecord.value;
    }

    get zoneLabel() {
        return this.parent.zoneGeographyCode;
    }

    get abRecord() {
        return this.allRecords
            .find(({ geographyCode }) => this.parent.abGeographyCode === geographyCode);
    }

    get abValue() {
        return this.abRecord && this.abRecord.value;
    }

    get abLabel() {
        return this.parent.abGeographyCode;
    }

    get bestRecord() {
        const condition = (this.options.positiveDirection === -1)
            ? (prev, curr) => ((prev.value < curr.value) ? prev : curr) // Lowest val is best
            : (prev, curr) => ((prev.value > curr.value) ? prev : curr); // Highest val is best
        const records = this.allRecords
            .filter((record) => record.urbanRuralClass === this.parent.urbanRuralClass)
            .filter((record) => record.value !== null);
        return (records.length) ? records.reduce(condition) : null;
    }

    get bestValue() {
        return this.bestRecord && this.bestRecord.value;
    }

    get bestLabel() {
        const urc = this.bestRecord.urbanRuralClass;
        if (urc.endsWith('Influence')) {
            return similarAreaLabel;
        }
        if (urc.endsWith('Area')) {
            return urc;
        }
        return `${urc} Area`;
    }


    get valueUnits() {
        return this.options.units;
    }

    get domain() {
        if (this.options.domain === 'max') {
            return [0, Math.max(...this.allRecords.map(({ value }) => value))];
        }
        return this.options.domain;
    }

    get isNull() {
        return this.value === undefined || this.value === null;
    }

    // eslint-disable-next-line class-methods-use-this
    get secondaryLabel() {
        // TODO: Actually implement this
        return '';
    }
}
